/**
 * Web component: Voting section (voting-section)
 * -----------------------------------------------------------------------------
 * Section with 2 options to choose from and an email field to send the data.
 * - Component settings: section / limited / web / vue
 *
 */

import './voting-section.scss';

import { vueReady } from '~/core/runners';
import votingTotalQuery from '~graphql/voting-total.gql';
import * as webComponent from '~/helpers/web-components';

/**
 * DOM selectors.
 */
const selectors = {
  form: '[js-voting-section="form"]',
  submit: '[js-voting-section="submit"]',
  emailInput: '[js-voting-section="email-input"]',
  emailError: '[js-voting-section="email-error"]',
  title: '[js-voting-section="title"]',
  description: '[js-voting-section="description"]',
  votingResults: '[js-voting-section="results"]',
  result1: '[js-voting-section="result-1"]',
  result2: '[js-voting-section="result-2"]',
  progress1: '[js-voting-section="progress-1"]',
  progress2: '[js-voting-section="progress-2"]',
  percentage1: '[js-voting-section="percentage-1"]',
  percentage2: '[js-voting-section="percentage-2"]',
};

/**
 * Create new <voting-section> custom element.
 */
class VotingSection extends HTMLElement {
  static observedAttributes = [];

  attributeChangedCallback(name, oldValue, newValue) {
    webComponent.handleAttributeUpdate(this, name, newValue, oldValue);
  }

  data(component) {
    return {
      selectors: {
        form: component.querySelector(selectors.form),
        submit: component.querySelector(selectors.submit),
        emailInput: component.querySelector(selectors.emailInput),
        emailError: component.querySelector(selectors.emailError),
        title: component.querySelector(selectors.title),
        description: component.querySelector(selectors.description),
        votingResults: component.querySelector(selectors.votingResults),
        result1: component.querySelector(selectors.result1),
        result2: component.querySelector(selectors.result2),
        progress1: component.querySelector(selectors.progress1),
        progress2: component.querySelector(selectors.progress2),
        percentage1: component.querySelector(selectors.percentage1),
        percentage2: component.querySelector(selectors.percentage2),
      },
    };
  }

  computed() {
    return {
      // Computed properties
      // computedProp() {
      //   return ''
      // },
    };
  }

  watch() {
    return {
      // Watch functions
      // watchedProp(newValue, oldValue) {
      //   // Not empty
      // },
    };
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = {};
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = this.data(this);
    webComponent.setup(this);

    this.setEventListeners();

    if (!this.$data.selectors.votingResults?.classList.contains('is-hidden')) {
      this.getVotingCount(false);
    }
  }

  /**
   * Component lifecycle - unmounted.
   */
  disconnectedCallback() {
    // Remove event listeners
  }

  /**
   * Set event listeners.
   * - Must pass 'this' as argument for function to have access to component.
   */
  setEventListeners() {
    if (!this.$data.selectors.form) {
      return;
    }

    this.$data.selectors.form.addEventListener('submit', (event) => {
      this.handleSubmit(event);
    });

    this.$data.selectors.emailInput?.addEventListener('focus', () => {
      this.clearEmailError();
    });
  }

  /**
   * Handle form submission.
   */
  handleSubmit(event) {
    event.preventDefault();

    const email = this.$data.selectors.emailInput?.value ?? '';
    const selectedVote = this.$data.selectors.form.querySelector(
      'input[type="radio"]:checked',
    )?.value;

    const emailRegex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

    if (!emailRegex.test(email)) {
      this.$data.selectors.emailInput?.classList.add('has-error');
      this.$data.selectors.emailError?.classList.remove('is-hidden');
      return;
    }

    const formData = new FormData();
    formData.append('email', email);
    formData.append('voting', selectedVote);

    this.$data.selectors.submit?.classList.add('is-loading');
    this.$data.selectors.submit?.setAttribute('disabled', '');

    fetch('/apps/challenge/customer/vote', {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.getVotingCount(true, true);
        } else {
          this.$data.selectors.emailInput?.classList.add('has-error');
          this.$data.selectors.emailError?.classList.remove('is-hidden');
        }
      })
      .catch((error) => {
        cnvs.ReportError('Error submitting vote:', error);
        this.$data.selectors.emailInput?.classList.add('has-error');
        this.$data.selectors.emailError?.classList.remove('is-hidden');
      })
      .finally(() => {
        this.$data.selectors.submit?.classList.remove('is-loading');
        this.$data.selectors.submit?.removeAttribute('disabled');
      });
  }

  async getVotingCount(updateHeader = true, scheduleReload = false) {
    try {
      const response = await cnvs.Query({
        query: votingTotalQuery,
        variables: {
          country: cnvs.store.country,
          language: cnvs.store.language,
        },
      });

      const data = JSON.parse(response.shop.metafield.value);

      const dark = data?.dark ?? 0;
      const white = data?.white ?? 0;
      const total = data.countedVotes || dark + white;

      const darkPercentage = total > 0 ? Math.round((dark / total) * 100) : 50;
      const whitePercentage = 100 - darkPercentage;

      this.$data.selectors.progress1.style.width = `${darkPercentage}%`;
      this.$data.selectors.progress2.style.width = `${whitePercentage}%`;

      this.$data.selectors.percentage1.textContent = `${darkPercentage}%`;
      this.$data.selectors.percentage2.textContent = `${whitePercentage}%`;

      this.$data.selectors.result1?.classList.toggle('is-winner', dark > white);
      this.$data.selectors.result2?.classList.toggle('is-winner', white > dark);

      if (updateHeader) {
        this.$data.selectors.title.textContent =
          this.$data.selectors.title.dataset.successTitle;
        this.$data.selectors.description.textContent =
          this.$data.selectors.description.dataset.successDescription;
      }

      this.$data.selectors.form.classList.add('is-hidden');
      this.$data.selectors.votingResults.classList.remove('is-hidden');

      if (scheduleReload) {
        const enabled = this.dataset.enableReload === 'true';
        const delay = parseInt(this.dataset.reloadDelay || '0');

        if (enabled && delay > 0) {
          setTimeout(() => location.reload(), delay);
        }
      }
    } catch (error) {
      cnvs.ReportError('Error fetching total votes:', error);
    } finally {
      this.$data.selectors.submit?.classList.remove('is-loading');
      this.$data.selectors.submit?.removeAttribute('disabled');
    }
  }

  /**
   * Clear email error state.
   */
  clearEmailError() {
    this.$data.selectors.emailInput?.classList.remove('has-error');
    this.$data.selectors.emailError?.classList.add('is-hidden');
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('voting-section')) {
    return;
  }

  customElements.define('voting-section', VotingSection);
});
